import {
  CssCustomPropertyGroupStyleItem,
  CssPropertyGroupStyleItem,
  StylesGroup,
  StyleValues,
} from '../types';
import { parseFilterValue } from '../../../apis/services/editActionsServices/nativeActions/globalDesign/filter/parser';
import { VirtualManifestAPI } from '@wix/editor-platform-manifest-services';
import { buildRootContextInfosMap } from '../common/grouping/helpers/buildRootContextInfosMap';
import { getAllowedPropertiesForAction } from '../../../apis/services/editActionsServices/nativeActions/globalDesign/constants';
import { StylesDerivedNativeActions } from '@wix/editor-platform-host-integration-apis';
import { isAllowedStylePresent } from '../../../apis/services/editActionsServices/nativeActions/globalDesign/common/actionValidators/actionVisibilityRules';

/**
 * Filter group display filter
 *
 * Filters component groups in the filter panel based on filter values.
 * A meaningful filter value is a filter with a drop shadow that has a blur radius.
 *
 * Logic for each root component and its children:
 * 1. If ANY group (root or child) has a meaningful filter value:
 *    - Show only groups that have a meaningful filter value (filters both roots and children)
 * 2. If NO group has a meaningful filter value:
 *    - Show only root groups that are valid for filter properties (have manifest support)
 *    - Hide child groups
 */
export const filterGroupDisplayFilter = (
  groups: StylesGroup[],
  rootContexts: VirtualManifestAPI[],
): StylesGroup[] => {
  if (groups.length === 0) {
    return groups;
  }

  const result: StylesGroup[] = [];
  const rootContextInfos = buildRootContextInfosMap(groups, rootContexts);

  for (const rootContextInfo of Object.values(rootContextInfos)) {
    const contextGroups = [
      ...rootContextInfo.rootGroups,
      ...rootContextInfo.childGroups,
    ];
    const isSomeShadowVisible = contextGroups.some(hasVisibleFilter);

    if (isSomeShadowVisible) {
      result.push(...contextGroups.filter(hasVisibleFilter));
      continue;
    }

    for (const rootGroup of rootContextInfo.rootGroups) {
      if (
        isAllowedStylePresent(
          rootGroup.context,
          new Set(
            getAllowedPropertiesForAction(
              StylesDerivedNativeActions.filterStyles,
            ),
          ),
          rootGroup.styleNamespace,
        )
      ) {
        result.push(rootGroup);
      }
    }
  }

  return result;
};

const hasVisibleFilter = (group: StylesGroup): boolean => {
  const context = group.context;
  const styles = context.query.getStyles();
  if (group.styleNamespace === 'cssProperties') {
    return hasVisibleFilterInCssProperties(group.items, styles);
  }

  return hasVisibleFilterInCssCustomProperties(group.items, styles);
};

const hasVisibleFilterInCssProperties = (
  items: CssPropertyGroupStyleItem[],
  styles: StyleValues,
): boolean => {
  const itemsIds = new Set(items.map((item) => item.styleItemId));

  if (styles.cssProperties?.filter && itemsIds.has('filter')) {
    const parsed = parseFilterValue(styles.cssProperties.filter);
    if (parsed.dropShadow.blurRadius) {
      return true;
    }
  }

  return false;
};

const hasVisibleFilterInCssCustomProperties = (
  items: CssCustomPropertyGroupStyleItem[],
  styles: StyleValues,
): boolean => {
  for (const item of items) {
    const cssPropertyType = item.styleItem.cssPropertyType;
    if (
      cssPropertyType &&
      cssPropertyType === 'filter' &&
      styles.cssCustomProperties[item.styleItemId]
    ) {
      const parsed = parseFilterValue(
        styles.cssCustomProperties[item.styleItemId],
      );

      if (parsed.dropShadow.blurRadius) {
        return true;
      }
    }
  }

  return false;
};
